/** @format */

import {
	FeedbackResponse,
	Helpful,
	ProductReviewed,
	ProductReviewsProsCons,
	ProductStatisticsResult,
	RatingSummaryResponse,
	Review,
	ReviewMedia,
	ReviewPagination,
	ReviewRequest,
	ReviewRequestKey,
	ReviewReturn,
	ReviewsPaginationReturn,
	ReviewsResponse,
	Statistics,
	StatisticsResponse,
	TagDistribution,
	TagDistributionData,
	TagDistributionInformation,
	TagDistributionValue
} from '../model';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Logger } from '@shared/data-access/services';
import { ReviewDao } from '../dao/reviews.dao';

@Injectable({
	providedIn: 'root'
})
export class ReviewService {
	constructor(
		private reviewDao: ReviewDao,
		private logger: Logger
	) {}

	/**
	 * @description
	 * Get reviews data from BazaarVoice for a given product by ID
	 */
	getReviews$(productId: string, options?: ReviewRequest): Observable<ReviewReturn> {
		return this.reviewDao.getReviewsByProductId$(productId, options).pipe(
			map((response: RatingSummaryResponse) => {
				const returnReviews: Review[] = response.Results;
				const data = response.Includes.Products
					? response.Includes.Products[productId] ||
						response.Includes.Products[productId.toLowerCase()] ||
						response.Includes.Products[productId.toUpperCase()]
					: null;

				const reviewSummary = {
					reviewCount: 0,
					overallRatingRange: 0,
					averageRating: 0,
					recommendedCount: 0,
					notRecommendedCount: 0,
					totalRecommendationsCount: 0,
					recommendationPercentage: 0,
					secondaryRatings: [],
					ratingDistribution: [],
					tagDistribution: null,
					category: '',
					brand: ''
				};

				const product: ProductReviewed = {
					productIdentifier: '',
					name: ''
				};

				if (data) {
					reviewSummary.reviewCount = data.ReviewStatistics.TotalReviewCount;
					reviewSummary.averageRating = data.ReviewStatistics.AverageOverallRating;
					reviewSummary.overallRatingRange = data.ReviewStatistics.OverallRatingRange;
					reviewSummary.ratingDistribution = [
						...data.ReviewStatistics.RatingDistribution
					];
					reviewSummary.recommendedCount = data.ReviewStatistics.RecommendedCount;
					reviewSummary.notRecommendedCount = data.ReviewStatistics.NotRecommendedCount;
					reviewSummary.category = data.CategoryId;
					reviewSummary.brand = data.BrandExternalId;
					reviewSummary.totalRecommendationsCount =
						data.ReviewStatistics.RecommendedCount +
						data.ReviewStatistics.NotRecommendedCount;
					reviewSummary.recommendationPercentage =
						reviewSummary.totalRecommendationsCount > 0
							? (reviewSummary.recommendedCount /
									reviewSummary.totalRecommendationsCount) *
								100
							: 0;
					reviewSummary.ratingDistribution.sort((a, b) => b.RatingValue - a.RatingValue);
					reviewSummary.tagDistribution = this.extractTagDistribution(
						data.ReviewStatistics.TagDistribution
					);

					for (const key of data.ReviewStatistics.SecondaryRatingsAveragesOrder) {
						reviewSummary.secondaryRatings.push({
							label: data.ReviewStatistics.SecondaryRatingsAverages[key].Label,
							averageRating:
								data.ReviewStatistics.SecondaryRatingsAverages[key].AverageRating,
							minLabel:
								data.ReviewStatistics.SecondaryRatingsAverages[key].MinLabel ||
								'Poor',
							maxLabel:
								data.ReviewStatistics.SecondaryRatingsAverages[key].MaxLabel ||
								'Excellent',
							valueRange:
								data.ReviewStatistics.SecondaryRatingsAverages[key].ValueRange,
							type: data.ReviewStatistics.SecondaryRatingsAverages[key].DisplayType,
							valueLabel:
								data.ReviewStatistics.SecondaryRatingsAverages[key].ValueLabel
						});
					}

					product.name = data.Name || '';
					product.productIdentifier = productId;
				}
				if (response.HasErrors) {
					this.logger.dynamicallyLog(
						ReviewRequestKey.REVIEWS,
						productId,
						response.Errors
					);
				}
				return {
					reviews: this.transformResults(returnReviews ?? []),
					reviewSummary,
					product
				};
			}),
			catchError((error: HttpErrorResponse) => {
				this.logger.dynamicallyLog(ReviewRequestKey.REVIEWS, productId, error);
				return throwError(() => error);
			})
		);
	}

	/**
	 * @description
	 * Get AI review summary data from BazaarVoice for a given product by ID
	 */
	getAIReviewSummary$(productId: string) {
		return this.reviewDao.getReviewAISummaryByProductId$(productId).pipe(
			catchError((error: HttpErrorResponse) => {
				this.logger.dynamicallyLog(ReviewRequestKey.AISUMMARY, productId, error);
				return throwError(() => error);
			})
		);
	}

	/**
	 * @description
	 * Get reviews with Media data from BazaarVoice for a given product by ID
	 */

	getReviewsMultimedia$(productId: string): Observable<Review[]> {
		return this.reviewDao.getReviewsMultimedia$(productId).pipe(
			map((response: ReviewsResponse) => this.transformResults(response?.Results ?? [])),
			catchError((error: HttpErrorResponse) => {
				this.logger.dynamicallyLog(ReviewRequestKey.MULTIMEDIA, productId, error);
				return throwError(() => error);
			})
		);
	}

	/**
	 * @description
	 * Get statistics data from BazaarVoice for a given product by ID
	 */
	getStatistics$(productId: string): Observable<Statistics> {
		return this.reviewDao.getStatisticsByProductId$(productId).pipe(
			map((response: StatisticsResponse) => {
				let productStats: ProductStatisticsResult['ProductStatistics'] | null = null;

				if (Array.isArray(response.Results) && response.Results.length > 0) {
					productStats = response.Results[0].ProductStatistics;
				}

				const returnData: Statistics = {
					totalQuestionCount: productStats?.QAStatistics?.TotalQuestionCount || 0,
					totalAnswerCount: productStats?.QAStatistics?.TotalAnswerCount || 0
				};

				return returnData;
			}),
			catchError((error: HttpErrorResponse) => {
				this.logger.dynamicallyLog(ReviewRequestKey.STATISTICS, productId, error);
				return throwError(() => error);
			})
		);
	}

	/* Helper methods */
	/**
	 * @description
	 * Get reviews with Media data from BazaarVoice for a given product by ID
	 */

	getReviewsResults$(
		productId: string,
		options: ReviewRequest = { offset: 0, limitTo: 15 }
	): Observable<ReviewsPaginationReturn> {
		return this.reviewDao.getListReviews$(productId, options).pipe(
			map(({ Offset, Results, TotalResults }) => ({
				reviews: this.transformResults(Results ?? []),
				pagination: this.createPagination(TotalResults, Offset, options.limitTo)
			})),
			catchError((error: HttpErrorResponse) => {
				this.logger.dynamicallyLog(ReviewRequestKey.LIST, productId, error);
				return throwError(() => error);
			})
		);
	}

	/**
	 * @description
	 * A function that takes a "helpful" parameter and returns an Observable
	 * that sets and submits feedback using the provided parameter, then returns a response of type FeedbackResponse
	 */
	submitFeedback$(helpful: Helpful): Observable<FeedbackResponse> {
		return this.reviewDao.setSubmitFeedback$(helpful).pipe(
			catchError((error: HttpErrorResponse) => {
				this.logger.dynamicallyLog(ReviewRequestKey.SUBMITFEEDBACK, '', error);
				return throwError(() => error);
			})
		);
	}

	/* Helper methods */
	/**
	 * @description
	 * Get TagDistribution data from BazaarVoice and transform in the data that will be stored
	 */
	extractTagDistribution(tagDistribution: Partial<TagDistributionData>) {
		if (Object.keys(tagDistribution).length) {
			const tagDistributionEntries = Object.entries({ ...tagDistribution });

			const tagDistributionSummary: TagDistribution = tagDistributionEntries.reduce(
				(transformedData, [key, { Id, Label, Values }]) => {
					const currentKey =
						key === 'Pro' ? TagDistributionValue.PRO : TagDistributionValue.CON;

					const frequencies = [...Values].map((frequency) => ({
						count: frequency.Count,
						name: frequency.Value
					}));

					const tagDistributionValues: TagDistributionInformation = {
						id: Id,
						label: Label,
						frequencies
					};

					return { ...transformedData, [currentKey]: tagDistributionValues };
				},
				{} as TagDistribution
			);

			return tagDistributionSummary;
		}
		return null;
	}

	createPagination(total: number, offset: number, itemsPerPage: number): ReviewPagination {
		const currentPage = offset >= total ? -1 : offset / itemsPerPage + 1;
		const lastPage = Math.ceil(total / itemsPerPage);
		const secondLastPage = total <= itemsPerPage ? lastPage : lastPage - 1;

		return {
			total,
			currentPage,
			secondLastPage,
			lastPage,
			perPage: itemsPerPage,
			firstPage: 1,
			from: (currentPage - 1) * itemsPerPage + 1,
			to: currentPage * itemsPerPage
		};
	}

	extractUrlsThumbnail(review: Review): ReviewMedia[] {
		const medias: ReviewMedia[] = [];
		review.Photos.forEach((image) => {
			medias.push({
				url: image.Sizes.normal.Url,
				id: image.Id,
				reviewId: review.Id
			});
		});
		review.Videos.forEach((video) => {
			let videoThumbnailUrl = video.VideoThumbnailUrl;
			if (!!video.VideoHost && video.VideoHost.toLowerCase().includes('youtube')) {
				videoThumbnailUrl = `https://img.youtube.com/vi/${video.VideoId}/default.jpg`;
			}
			medias.push({
				url: videoThumbnailUrl,
				id: video.VideoId,
				video: video,
				reviewId: review.Id
			});
		});
		return medias;
	}

	transformResults(paginatedReviews: Review[]): Review[] {
		const reviewMedia: Review[] = [];
		for (const paginatedReview of paginatedReviews) {
			const review = { ...paginatedReview };
			review.Media = this.extractUrlsThumbnail(review);
			reviewMedia.push(review);
		}
		return reviewMedia;
	}

	/**
	 * @description
	 * Get reviews data from BazaarVoice for a given product by ID
	 */
	getProsConsByProductId$(productId: string): Observable<ProductReviewsProsCons> {
		return this.reviewDao.getProsConsByProductId$(productId).pipe(
			catchError((error: HttpErrorResponse) => {
				this.logger.dynamicallyLog(ReviewRequestKey.PROSCONS, productId, error);
				return throwError(() => error);
			})
		);
	}
}
